import React, { useContext, useId, useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DetailButton } from '../detail'
import './form.css'

type FormPageHeadProps = {
  eyebrow: string
  title: string
  subtitle?: string
  backLabel: string
  onBack?: () => void
  crumbs?: React.ReactNode
  titleTourId?: string
  extra?: React.ReactNode
}

export function FormPageHead({
  eyebrow,
  title,
  subtitle,
  backLabel,
  onBack,
  crumbs,
  titleTourId,
  extra,
}: FormPageHeadProps) {
  const navigate = useNavigate()
  return (
    <div className="incyia-form-page">
      {crumbs ? <div className="incyia-form-crumbs">{crumbs}</div> : null}
      <div className="incyia-form-head">
        <div>
          <p className="incyia-form-eyebrow">{eyebrow}</p>
          <h1 className="incyia-form-title" data-tour-id={titleTourId}>
            {title}
          </h1>
          {subtitle ? <p className="incyia-form-sub">{subtitle}</p> : null}
        </div>
        <div className="incyia-form-head-actions">
          {extra}
          <DetailButton
            variant="ghost"
            onClick={onBack || (() => navigate(-1))}
          >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            aria-hidden
          >
            <path d="M10 3.5L5.5 8 10 12.5" strokeLinecap="round" />
          </svg>
          {backLabel}
        </DetailButton>
        </div>
      </div>
    </div>
  )
}

type FormSectionProps = {
  title: string
  description?: string
  children: React.ReactNode
}

export function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <section className="incyia-fsec">
      <div className="incyia-fsec-head">
        <h4>{title}</h4>
        {description ? <p>{description}</p> : null}
      </div>
      {children}
    </section>
  )
}

type FormGridProps = {
  cols?: 1 | 2 | 3
  children: React.ReactNode
}

export function FormGrid({ cols = 2, children }: FormGridProps) {
  const cls =
    cols === 1
      ? 'incyia-fgrid incyia-fgrid--one'
      : cols === 3
        ? 'incyia-fgrid incyia-fgrid--three'
        : 'incyia-fgrid'
  return <div className={cls}>{children}</div>
}

type FormFieldProps = {
  label: string
  required?: boolean
  help?: string
  span?: boolean
  children: React.ReactNode
}

type RequiredCountValue = {
  count: number
  register: (id: string, required: boolean) => void
}

const RequiredCountContext = React.createContext<RequiredCountValue | null>(
  null
)

export function RequiredFieldsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [flags, setFlags] = useState<Record<string, boolean>>({})
  const register = React.useCallback((id: string, required: boolean) => {
    setFlags((prev) => {
      if (Boolean(prev[id]) === required) return prev
      return { ...prev, [id]: required }
    })
  }, [])
  const count = useMemo(
    () => Object.values(flags).filter(Boolean).length,
    [flags]
  )
  const value = useMemo(() => ({ count, register }), [count, register])
  return (
    <RequiredCountContext.Provider value={value}>
      {children}
    </RequiredCountContext.Provider>
  )
}

function useLiveRequiredCount(): number | null {
  return useContext(RequiredCountContext)?.count ?? null
}

export function FormField({
  label,
  required,
  help,
  span,
  children,
}: FormFieldProps) {
  const ctx = useContext(RequiredCountContext)
  const register = ctx?.register
  const id = useId()
  useLayoutEffect(() => {
    if (!register) return
    register(id, Boolean(required))
    return () => register(id, false)
  }, [register, id, required])
  return (
    <div className={`incyia-ffield${span ? ' incyia-ffield--span' : ''}`}>
      <label className="incyia-ffield-label">
        {label}
        {required ? <span className="incyia-ffield-req">*</span> : null}
      </label>
      {children}
      {help ? <span className="incyia-ffield-help">{help}</span> : null}
    </div>
  )
}

export function FormNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="incyia-form-note">
      <svg
        width="15"
        height="15"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden
      >
        <circle cx="8" cy="8" r="5.5" />
        <path d="M8 7.2v3.5M8 5.2h.01" strokeLinecap="round" />
      </svg>
      <span>{children}</span>
    </div>
  )
}

type FormAsideStep = {
  title: string
  fieldCount: number
}

type FormAsideProps = {
  title: string
  steps: FormAsideStep[]
  requiredCount: number
  submitLabel: string
  tipTitle: string
  tip: string
  /** Replaces the generic tip while a parent mission, project, or plan is selected. */
  context?: React.ReactNode
  fieldsLabel: (n: number) => string
  requiredNote: (n: number, submit: string) => string
}

export function FormAside({
  title,
  steps,
  requiredCount,
  submitLabel,
  tipTitle,
  tip,
  context,
  fieldsLabel,
  requiredNote,
}: FormAsideProps) {
  const liveRequired = useLiveRequiredCount()
  const shownRequired =
    liveRequired == null || liveRequired === 0 ? requiredCount : liveRequired
  return (
    <aside className="incyia-form-aside">
      <div className="incyia-aside-card">
        <h4>{tipTitle}</h4>
        <div className="incyia-aside-body">
          {context ?? <p className="incyia-aside-note">{tip}</p>}
        </div>
      </div>
      <div className="incyia-aside-card">
        <h4>{title}</h4>
        <div className="incyia-aside-body">
          {steps.map((step, i) => (
            <div key={step.title} className="incyia-form-step">
              <span className="incyia-form-step-n">{i + 1}</span>
              <div>
                <b>{step.title}</b>
                <span>{fieldsLabel(step.fieldCount)}</span>
              </div>
            </div>
          ))}
          <p className="incyia-aside-note incyia-aside-divider">
            {requiredNote(shownRequired, submitLabel)}
          </p>
        </div>
      </div>
    </aside>
  )
}

type FormFootProps = {
  note: string
  cancelLabel: string
  submitLabel: string
  onCancel?: () => void
  onSubmit: () => void
  submitDisabled?: boolean
}

export function FormFoot({
  note,
  cancelLabel,
  submitLabel,
  onCancel,
  onSubmit,
  submitDisabled,
}: FormFootProps) {
  const navigate = useNavigate()
  return (
    <div className="incyia-form-foot">
      <span className="incyia-form-foot-left">{note}</span>
      <span className="incyia-form-foot-right">
        <DetailButton
          variant="ghost"
          onClick={onCancel || (() => navigate(-1))}
        >
          {cancelLabel}
        </DetailButton>
        <DetailButton onClick={onSubmit} disabled={submitDisabled}>
          {submitLabel}
        </DetailButton>
      </span>
    </div>
  )
}

export function FormShell({ children }: { children: React.ReactNode }) {
  return <div className="incyia-form-shell">{children}</div>
}

export function FormLayout({
  main,
  aside,
}: {
  main: React.ReactNode
  aside: React.ReactNode
}) {
  return (
    <div className="incyia-form-layout">
      {main}
      {aside}
    </div>
  )
}
